/**
 * Use Case Selector Component
 * 
 * This component provides a dropdown for selecting a use case.
 * It displays use cases based on the selected client and facility.
 */

import { useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Label } from "../../ui/label";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useUseCases, UseCase } from "../../../lib/api";

interface UseCaseSelectorProps {
  className?: string;
  isTarget?: boolean;
  label?: string;
  client?: any;
  facilityId?: string | null;
}

export function UseCaseSelector({ 
  className,
  isTarget = false,
  label = "Use Case",
  client,
  facilityId
}: UseCaseSelectorProps) {
  const { 
    selectedFacilityId, 
    selectedUseCaseId, 
    setSelectedUseCaseId,
    selectedClient,
    targetFacilityId,
    targetUseCaseId,
    setTargetUseCaseId,
    targetClient
  } = useWorkflowStore();
  
  // Use the appropriate client, facility ID, and use case ID based on isTarget
  const currentClient = client || (isTarget ? targetClient : selectedClient);
  const currentFacilityId = facilityId || (isTarget ? targetFacilityId : selectedFacilityId);
  const currentUseCaseId = isTarget ? targetUseCaseId : selectedUseCaseId;
  const setUseCaseId = isTarget ? setTargetUseCaseId : setSelectedUseCaseId;
  
  // Fetch use cases using the new hook
  const { 
    data: useCases = [], 
    isLoading, 
    error 
  } = useUseCases(currentClient, currentFacilityId);
  
  // Reset selected use case when facility or client changes
  useEffect(() => {
    setUseCaseId(null);
  }, [currentFacilityId, currentClient, setUseCaseId]);
  
  // Handle use case selection
  const handleUseCaseChange = (value: string) => {
    setUseCaseId(value);
  };
  
  // Determine if the dropdown should be disabled
  const isDisabled = !currentFacilityId || !currentClient || isLoading;
  
  // Determine the placeholder text based on state
  const getPlaceholderText = () => {
    if (!currentClient) return isTarget ? "Select a client first" : "Select a source client first";
    if (!currentFacilityId) return isTarget ? "Select a facility first" : "Select a source facility first";
    return isTarget ? "Select use case" : "Select source use case";
  };
  
  return (
    <div className={className}>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor={isTarget ? "target-use-case" : "use-case"}>{label}</Label>
        <Select
          value={currentUseCaseId || ""}
          onValueChange={handleUseCaseChange}
          disabled={isDisabled}
        >
          <SelectTrigger id={isTarget ? "target-use-case" : "use-case"}>
            <SelectValue placeholder={getPlaceholderText()} />
          </SelectTrigger>
          <SelectContent>
            {isLoading ? (
              <SelectItem value="loading" disabled>
                Loading use cases...
              </SelectItem>
            ) : error ? (
              <SelectItem value="error" disabled>
                Error: {error instanceof Error ? error.message : "Failed to load use cases"}
              </SelectItem>
            ) : useCases.length === 0 ? (
              <SelectItem value="none" disabled>
                No use cases available
              </SelectItem>
            ) : (
              useCases.map((useCase: UseCase) => (
                <SelectItem key={useCase.id} value={useCase.id}>
                  {useCase.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
